import { Link } from "react-router-dom";
import { Phone, Mail, Clock, MapPin, ArrowRight, Instagram, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import Logo from "@/components/Logo";
import { site } from "@/lib/site";
import { services } from "@/lib/services-data";

const Footer = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <footer className="bg-accent text-accent-foreground">
      {/* CTA Banner */}
      <div className="bg-primary relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-30" />
        <div className="container-max px-4 py-12 md:py-16 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl text-primary-foreground font-bold tracking-tight">
                Ready for a fence that lasts?
              </h3>
              <p className="text-primary-foreground/80 text-sm mt-1">
                Free, no-pressure estimates across Dillon & Summit County.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="secondary"
                size="lg"
                onClick={openQuoteForm}
                className="gap-2 rounded-xl font-bold"
              >
                Get Free Estimate <ArrowRight className="h-5 w-5" />
              </Button>
              <a href={site.phoneHref}>
                <Button
                  size="lg"
                  className="gap-2 rounded-xl font-bold bg-white text-primary hover:bg-white/90"
                >
                  <Phone className="h-4 w-4" /> {site.phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer content */}
      <div className="container-max section-padding">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="mb-4 inline-flex items-center justify-center rounded-2xl bg-white p-4 shadow-premium">
              <Logo />
            </div>
            <p className="text-accent-foreground/75 text-sm leading-relaxed">
              Professional fence installation, repair, and custom fencing built
              for Colorado's high country — proudly serving Dillon and Summit County.
            </p>
            <div className="mt-4 bg-secondary/10 border border-secondary/20 rounded-xl px-4 py-3">
              <p className="text-secondary font-semibold text-sm flex items-start gap-2">
                <Gift className="h-4 w-4 shrink-0 mt-0.5" />
                Refer a neighbor & earn a reward
              </p>
            </div>
            <a
              href={site.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 text-accent-foreground/75 hover:text-secondary text-sm transition-colors"
            >
              <Instagram className="h-4 w-4" /> @summitneighborfencing
            </a>
          </div>

          <div>
            <h4 className="font-heading text-lg tracking-tight mb-4 text-accent-foreground/80 font-bold">
              Our Services
            </h4>
            <ul className="space-y-2.5">
              {services.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="footer-link-underline text-accent-foreground/75 hover:text-secondary text-sm transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-lg tracking-tight mb-4 text-accent-foreground/80 font-bold">
              Contact
            </h4>
            <ul className="space-y-3 text-sm text-accent-foreground/80">
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 shrink-0 text-secondary" />
                <a href={site.phoneHref} className="hover:text-secondary transition-colors">{site.phone}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0 text-secondary" />
                <a href={site.emailHref} className="hover:text-secondary transition-colors break-all">{site.email}</a>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-secondary" />
                <span>{site.serviceArea}</span>
              </li>
            </ul>
            <div className="mt-4">
              <Link to="/locations" className="footer-link-underline text-secondary font-semibold text-sm">
                View all service areas →
              </Link>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-lg tracking-tight mb-4 text-accent-foreground/80 font-bold">
              Hours
            </h4>
            <ul className="space-y-2 text-sm text-accent-foreground/80">
              {site.hours.map((h) => (
                <li key={h.days} className="flex items-start gap-2">
                  <Clock className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                  <span>
                    <span className="block font-semibold text-accent-foreground/90">{h.days}</span>
                    <span className="text-accent-foreground/70">{h.time}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-accent-foreground/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-accent-foreground/60 text-xs">
            © {new Date().getFullYear()} {site.name} All rights reserved.
          </p>
          <span className="text-accent-foreground/60 text-xs">
            {site.serviceArea} · Local fencing contractor · Free estimates
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
